import { useEffect, useId, useState } from "react";
import type { PerformanceKind, Performer, Work } from "../api/types";
import { useServices } from "../app/services";
import { useAction } from "../app/useAction";
import { kindLabel } from "../lib/labels";
import { SEGMENT_PROBLEM_TEXT, checkSegment, formatMs, parseTime, todayInput } from "../lib/time";
import { Icon } from "./Icon";
import { Field } from "./ui";

/**
 * 登録・修正の入力部品。曲名だけで別の曲・歌唱を自動でまとめない（候補を見せて、選ぶのは本人）。
 * YouTube のチャンネル名を「歌った人」と同じに扱わない
 */

function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/* ---------------------------------------------------------------- 曲を選ぶ・新しく作る */

export function WorkPicker({ value, onChange, error }: { value: Work | null; onChange: (w: Work | null) => void; error?: string | null }) {
  const { api } = useServices();
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const [results, setResults] = useState<Work[]>([]);
  const connected = api.isConnected("works.search");
  const create = useAction("works.create", (title: string) => api.createWork({ title, aliases: [] }));
  const listId = useId();
  useEffect(() => {
    if (!connected || !dq.trim()) return setResults([]);
    let alive = true;
    api
      .searchWorks(dq)
      .then((r) => alive && setResults(r))
      .catch(() => alive && setResults([]));
    return () => {
      alive = false;
    };
  }, [api, dq, connected]);

  if (value) {
    return (
      <div className="picked">
        <span className="picked__label">曲</span>
        <span className="picked__value">{value.title}</span>
        <button type="button" className="btn btn-ghost btn-small" onClick={() => onChange(null)}>
          選び直す
        </button>
      </div>
    );
  }
  const exact = results.some((w) => w.title === q.trim());
  return (
    <Field
      label="曲名"
      hint="登録済みの曲から選びます。見つからなければ新しい曲として登録できます（同じ名前でも自動ではまとめません）。"
      error={error ?? create.error}
      required
    >
      {({ id, describedBy, invalid }) => (
        <div className="picker">
          <input
            id={id}
            className="input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            aria-controls={listId}
            autoComplete="off"
            disabled={!connected}
            placeholder={connected ? "曲名で探す" : "まだAPIに接続されていません"}
          />
          <ul id={listId} className="picker__list" aria-label="曲の候補">
            {results.map((w) => (
              <li key={w.id}>
                <button type="button" className="picker__option" onClick={() => onChange(w)}>
                  {w.title}
                  {(w.aliases ?? []).length > 0 && <small>（{(w.aliases ?? []).join("・")}）</small>}
                </button>
              </li>
            ))}
            {q.trim() && !exact && (
              <li>
                <button
                  type="button"
                  className="picker__option picker__option--new"
                  disabled={create.running || !create.connected}
                  onClick={async () => {
                    const r = await create.run(q.trim());
                    if (r.ok) onChange(r.value);
                  }}
                >
                  <Icon name="plus" className="inline-icon" />「{q.trim()}」を新しい曲として登録
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </Field>
  );
}

/* ---------------------------------------------------------------- 歌った人（複数） */

export function PerformerPicker({
  value,
  onChange,
  error,
}: {
  value: Performer[];
  onChange: (p: Performer[]) => void;
  error?: string | null;
}) {
  const { api } = useServices();
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const [results, setResults] = useState<Performer[]>([]);
  const connected = api.isConnected("performers.search");
  const create = useAction("performers.create", (name: string) => api.createPerformer({ name }));
  const listId = useId();
  useEffect(() => {
    if (!connected || !dq.trim()) return setResults([]);
    let alive = true;
    api
      .searchPerformers(dq)
      .then((r) => alive && setResults(r))
      .catch(() => alive && setResults([]));
    return () => {
      alive = false;
    };
  }, [api, dq, connected]);
  const add = (p: Performer) => {
    if (!value.some((x) => x.id === p.id)) onChange([...value, p]);
    setQ("");
  };
  const exact = results.some((p) => p.name === q.trim());
  return (
    <Field
      label="歌った人"
      hint="複数人で歌ったときは、全員を追加します。動画のチャンネル名とは別に選びます。"
      error={error ?? create.error}
      required
    >
      {({ id, describedBy, invalid }) => (
        <div className="picker">
          {value.length > 0 && (
            <ul className="chips" aria-label="選んだ歌った人">
              {value.map((p) => (
                <li key={p.id} className="chip">
                  {p.name}
                  <button
                    type="button"
                    className="chip__remove"
                    onClick={() => onChange(value.filter((x) => x.id !== p.id))}
                    aria-label={`${p.name} を外す`}
                  >
                    <Icon name="close" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <input
            id={id}
            className="input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            aria-controls={listId}
            autoComplete="off"
            disabled={!connected}
            placeholder={connected ? "名前で探す" : "まだAPIに接続されていません"}
          />
          <ul id={listId} className="picker__list" aria-label="歌った人の候補">
            {results
              .filter((p) => !value.some((x) => x.id === p.id))
              .map((p) => (
                <li key={p.id}>
                  <button type="button" className="picker__option" onClick={() => add(p)}>
                    {p.name}
                  </button>
                </li>
              ))}
            {q.trim() && !exact && (
              <li>
                <button
                  type="button"
                  className="picker__option picker__option--new"
                  disabled={create.running || !create.connected}
                  onClick={async () => {
                    const r = await create.run(q.trim());
                    if (r.ok) add(r.value);
                  }}
                >
                  <Icon name="plus" className="inline-icon" />「{q.trim()}」を新しく登録して追加
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </Field>
  );
}

/* ---------------------------------------------------------------- 種別 */

export function KindChoice({ value, onChange }: { value: PerformanceKind | null; onChange: (k: PerformanceKind) => void }) {
  const kinds: Array<{ id: PerformanceKind; note: string }> = [
    { id: "original", note: "曲を最初に出した歌唱" },
    { id: "cover", note: "歌ってみた・カバー" },
    { id: "live", note: "ライブ・イベントでの歌唱" },
    { id: "stream", note: "配信の歌枠の中の1曲" },
  ];
  return (
    <fieldset className="choice-group choice-group--grid">
      <legend>
        種別<span className="field__req">必須</span>
      </legend>
      {kinds.map((k) => (
        <label key={k.id} className="choice">
          <input type="radio" name="kind" checked={value === k.id} onChange={() => onChange(k.id)} />
          <span>
            {kindLabel(k.id)}
            <small>{k.note}</small>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

/* ---------------------------------------------------------------- 歌った日（任意） */

export function PerformedDateField({
  value,
  onChange,
  error,
  hint = "歌枠・ライブの日。分からなければ空のままで構いません。",
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string | null;
  hint?: string;
}) {
  return (
    <Field label="歌った日（任意）" hint={hint} error={error}>
      {({ id, describedBy, invalid }) => (
        <div className="input-row">
          <input
            id={id}
            className="input input--date"
            type="date"
            max={todayInput()}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
          />
          {value && (
            <button type="button" className="btn btn-secondary btn-small" onClick={() => onChange("")}>
              日付を消す
            </button>
          )}
        </div>
      )}
    </Field>
  );
}

/* ---------------------------------------------------------------- 区間（開始・終了）。0 <= 開始 < 終了 <= 元動画の長さ */

export interface RangeValue {
  start: string;
  end: string;
}

export function readRange(
  v: RangeValue,
  durationMs: number | null,
): { start: number | null; end: number | null; error: string | null; field: "start" | "end" | null } {
  const start = parseTime(v.start);
  const end = parseTime(v.end);
  if (start === null) return { start, end, error: "開始を「10:00」「1:02:03」のように入力してください。", field: "start" };
  if (end === null) return { start, end, error: "終了を「14:05」「1:06:10」のように入力してください。", field: "end" };
  const problem = checkSegment(start, end, durationMs);
  if (problem) return { start, end, error: SEGMENT_PROBLEM_TEXT[problem], field: problem === "start_negative" ? "start" : "end" };
  return { start, end, error: null, field: null };
}

export function RangeFields({
  value,
  onChange,
  durationMs,
  showErrors,
}: {
  value: RangeValue;
  onChange: (v: RangeValue) => void;
  durationMs: number | null;
  showErrors: boolean;
}) {
  const r = readRange(value, durationMs);
  const len = r.start !== null && r.end !== null && !r.error ? r.end - r.start : null;
  return (
    <div className="range">
      <div className="range__row">
        <Field label="開始" hint="元動画の中の時刻" error={showErrors && r.field === "start" ? r.error : null} required>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              className="input input--time"
              inputMode="numeric"
              autoComplete="off"
              value={value.start}
              onChange={(e) => onChange({ ...value, start: e.target.value })}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              placeholder="10:00"
            />
          )}
        </Field>
        <Field
          label="終了"
          hint={durationMs ? `動画の長さ ${formatMs(durationMs)}` : "動画の長さは未確認"}
          error={showErrors && r.field === "end" ? r.error : null}
          required
        >
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              className="input input--time"
              inputMode="numeric"
              autoComplete="off"
              value={value.end}
              onChange={(e) => onChange({ ...value, end: e.target.value })}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              placeholder="14:05"
            />
          )}
        </Field>
      </div>
      <p className="range__summary" aria-live="polite">
        {len !== null
          ? `区間の長さ：${formatMs(len)}`
          : durationMs === null
            ? "動画の長さを確認できないため、まだ登録を確定できません。"
            : ""}
      </p>
    </div>
  );
}
